import { LineChart } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AmazonSearchLink } from "@/components/shared/AmazonSearchLink";
import { TruncatedText } from "@/components/ui/truncated-text";
import type { DailyRanksKeyword } from "@/types/rankTracker";
import { KpsBreakdownPopover } from "@/components/keyword-bank/KpsBreakdownPopover";
import { buildKeywordKpsViewModel } from "../kpsViewModel";
import { RankHeatCell } from "./RankHeatCell";
import { AdPushCell } from "./AdPushCell";
import { DEFAULT_CUTOFFS, type AdPushCutoffs } from "./adPush";
import type { RankType } from "./RankTypeToggle";
import { formatDateAdded, formatMetricValue } from "./constants";

export interface KeywordRowProps {
  keyword: DailyRanksKeyword;
  /** `days` from the daily-ranks response -- one heat cell per entry, the same array the product
   * and theme rows above it are built from. */
  days: string[];
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
  /**
   * WEB-004 / FR-6. The current rank-type view. `"organic"` (the default) is byte-for-byte today:
   * the day cells are the exact existing organic cells and nothing else is rendered. `"sponsored"`
   * paints each day cell from the keyword's sponsored reading (indigo), with the per-day variant
   * marker; `"both"` keeps the organic cell and stacks a thin sponsored strip beneath it, with
   * "Org"/"Spn" labels in the Ad push cell (the last column left of the day columns -- FR-7, FR-10).
   */
  rankType?: RankType;
  /** AUT-497 slice B. The brand's ad-push cutoffs, threaded down from the table shell's
   * `useGetAdPushCutoffs`; defaults to `DEFAULT_CUTOFFS` until they load. Feeds the Ad push pill's
   * classifier (WEB-002). */
  cutoffs?: AdPushCutoffs;
  /** Fired by clicking the row itself or the chart-icon button -- both open the keyword detail
   * modal (WEB-007, a different directory). Selection state and the modal itself are out of scope
   * here; this row only exposes the affordance. */
  onOpenDetail?: () => void;
}

/**
 * The keyword row: a checkbox, the keyword text, an optional `Head term` badge, the chart-icon
 * button, the five real metric values, one keyword heat cell per day (no tooltip, no roll-up), and
 * `Date Added`.
 */
export function KeywordRow({
  keyword,
  days,
  selected = false,
  onSelectChange,
  rankType = "organic",
  cutoffs = DEFAULT_CUTOFFS,
  onOpenDetail,
}: KeywordRowProps) {
  return (
    <TableRow className="cursor-pointer" onClick={onOpenDetail}>
      {/* w-[340px]/py-2.5, same reasoning as ThemeRow -- matches the header/product-row column
          width and trims this row to the mockup's measured 41px. */}
      <TableCell sticky className="w-[340px] py-2.5">
        {/*
          AUT-524: two nested-drawer accent bars (WEB-010), because a keyword row sits inside both its
          product's drawer and its theme's: `left-5` continues the product's section (aligned with
          ThemeRow's single bar) and `left-10` marks its own theme's. Both are anchored to the sticky
          `<td>` itself with `!absolute -inset-y-px` so each spans the FULL row height and butts into
          the next row's bars as one continuous line -- the old spans were anchored to the inner flex
          div, which the cell's padding insets, so on taller rows the `-inset-y-3` overhang stopped
          short and the bars read as columns of dashes. `!absolute` overrides the sticky column's
          `[&>*]:relative`; `-inset-y-px` bridges the row's 1px bottom border.
        */}
        <span aria-hidden className="pointer-events-none !absolute -inset-y-px left-5 w-0.5 bg-primary" />
        <span aria-hidden className="pointer-events-none !absolute -inset-y-px left-10 w-0.5 bg-primary" />
        <div className="relative flex items-center gap-2 min-w-0 pl-10">
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={(checked) => onSelectChange?.(!!checked)}
              aria-label={`Select ${keyword.keyword}`}
            />
          </span>
          <TruncatedText className="min-w-0">{keyword.keyword}</TruncatedText>
          {/* AUT-496 Part 1: the ticket names this surface explicitly — "Keywords tab, Master List,
              Rank Tracker keyword rows, Landscapes". Deferred when AUT-497 was rewriting this file;
              added once that merged (#426). */}
          <AmazonSearchLink keyword={keyword.keyword} />
          {/* WEB-002: the Ad badge sits before the Head term badge (design capture §7.9). Driven
              only by `hasAdSpend` -- server-computed and window-scoped (FEATURE-001), so nothing is
              recomputed here and neither `adSpend` nor `adSpendTotal` is read for the badge. Colour
              is the prototype's Sponsored-Products accent (`bg-heat-sp-4`/`text-heat-sp-on-light`,
              signal-v3 RankHeatmapTable.tsx), not the green status scale an earlier capture read. */}
          {keyword.hasAdSpend && (
            <Badge
              variant="secondary"
              className="shrink-0 cursor-default bg-[hsl(var(--heat-sp-4))] text-[hsl(var(--heat-sp-on-light))] hover:bg-[hsl(var(--heat-sp-4))]"
              title="Active Sponsored Products targeting"
              data-testid="ad-badge"
            >
              Ad
            </Badge>
          )}
          {keyword.isHeadTerm && (
            <Badge variant="secondary" className="shrink-0">
              Head term
            </Badge>
          )}
        </div>
      </TableCell>

      {/* px-0/text-center: COL_CHART is only 40px wide (`prototype-extracted.md § 1`, which
          documents this exact cell as "a px-0 text-center cell"); the default `p-4` left 8px of
          content width for a 24px button, so the button overflowed the cell's padding box on one
          side instead of sitting centered with even breathing room on both. */}
      <TableCell className="py-2 px-0 text-center">
        {/* Item 12: 24x24 hit area (h-6 w-6), rounded-md, text-primary, hover reveals a rounded
            square via bg-muted -- the same treatment ThemeRow's and ProductRow's chart buttons use
            (WEB-006). */}
        <button
          type="button"
          aria-label={`View rank chart for ${keyword.keyword}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetail?.();
          }}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-primary transition-colors hover:bg-muted"
        >
          <LineChart className="h-4 w-4" />
        </button>
      </TableCell>

      {/*
        px-3/whitespace-nowrap on every value cell below (item 9 follow-up): under `table-fixed`
        each of these columns is only as wide as its `HeatmapHeaderRow` COL_* constant (e.g.
        COL_DATE = 104), so the default `p-4`'s 16px side padding plus the browser's default
        `white-space: normal` let a long value (`Jun 20, 2026` was the one that actually did this)
        wrap to two lines and silently inflate the whole row past its measured 41px. Matching the
        header's own 12px padding and adding `whitespace-nowrap` (the mockup does the same for
        `COL_DATE`, `prototype-extracted.md § 1`) keeps every value on one line regardless of which
        keyword's numbers are widest.
      */}
      <TableCell className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">
        <div className="flex items-center justify-center gap-1.5">
          {formatMetricValue(keyword.kps)}
          {/* Stops the click from reaching the row's own onClick (FR-11) -- same pattern as the
              checkbox above and the chart button below, both of which sit inside a row whose own
              click opens the detail view. */}
          <span onClick={(e) => e.stopPropagation()}>
            <KpsBreakdownPopover viewModel={buildKeywordKpsViewModel(keyword)} />
          </span>
        </div>
      </TableCell>
      <TableCell className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">
        {formatMetricValue(keyword.searchVolume)}
      </TableCell>
      <TableCell className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">
        {formatMetricValue(keyword.keywordSales)}
      </TableCell>
      <TableCell className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">
        {formatMetricValue(keyword.competitors)}
      </TableCell>
      {/* Rel. renders only its own value in every view. Slice A parked Both view's "Org"/"Spn"
          labels here because Rel. was then the last metric column before the day columns; inserting
          the Ad push column (below) makes the Ad push cell that column instead, so the labels moved
          into it (design capture § Composition consequence, FR-8). */}
      <TableCell className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">
        {formatMetricValue(keyword.relevance)}
      </TableCell>

      {/* AUT-497 slice B: the Ad push pill (keyword rows only), and -- in Both view -- the "Org"/"Spn"
          sub-row labels that annotate the stacked day cells to its right. */}
      <AdPushCell keyword={keyword} cutoffs={cutoffs} showRowLabels={rankType === "both"} />

      {days.map((day, i) =>
        rankType === "organic" ? (
          <RankHeatCell
            key={day}
            day={day}
            position={keyword.positions[i]}
            variant="keyword"
            siblingAsin={keyword.siblingAsins?.[i] ?? null}
          />
        ) : rankType === "sponsored" ? (
          <RankHeatCell
            key={day}
            day={day}
            position={keyword.sponsoredPositions[i]}
            variant="keyword"
            scale="sponsored"
            fromVariant={keyword.sponsoredRankFromVariant[i]}
            variantAsin={keyword.sponsoredRankVariantAsin[i]}
          />
        ) : (
          <RankHeatCell
            key={day}
            day={day}
            position={keyword.positions[i]}
            variant="keyword"
            siblingAsin={keyword.siblingAsins?.[i] ?? null}
            strip={{
              position: keyword.sponsoredPositions[i],
              fromVariant: keyword.sponsoredRankFromVariant[i],
              variantAsin: keyword.sponsoredRankVariantAsin[i],
            }}
          />
        ),
      )}

      <TableCell className="py-2.5 px-3 text-left text-muted-foreground whitespace-nowrap border-l border-border">
        {formatDateAdded(keyword.dateAdded)}
      </TableCell>
    </TableRow>
  );
}
